// mui
import {
  Alert,
  Button,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
} from '@mui/material';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import AdminTablePage from '@/pwa/templates/AdminTablePage/AdminTablePage';
import DataTable, {
  GridColDef,
  GridRenderCellParams,
} from '@/pwa/organisms/DataTable/DataTable';
import {
  EvaluationChip,
  evaluationChipLabel,
} from '@/pwa/atoms/EvaluationChip/EvaluationChip';
import {
  DetailList,
  DetailRow,
  DetailSection,
} from '@/pwa/molecules/DetailList/DetailList';

// models
import {
  Backtest,
  BacktestRunStatus,
  BacktestRunSummary,
  RunCounts,
} from '@karehero/models';
import { formatDateTime } from '@karehero/helpers/datetime';

// data
import {
  EMPTY_VALUE,
  TRANSCRIPT_CHOICE_LABEL,
  shortSha,
} from '@/pwa/pages/AdminBacktestsPage/data';

const ROWS_PER_PAGE = 25;

const countColumn = (
  field: keyof RunCounts,
  headerName: string,
): GridColDef => ({
  field,
  headerName,
  flex: 0.4,
  minWidth: 110,
  valueGetter: (_value: unknown, row: BacktestRunSummary) =>
    row.counts?.[field],
  renderCell: ({ value }: GridRenderCellParams) => value ?? EMPTY_VALUE,
});

const columns: GridColDef[] = [
  {
    field: 'requestedAt',
    headerName: 'Requested',
    flex: 0.7,
    minWidth: 160,
    renderCell: ({ value }: GridRenderCellParams) => formatDateTime(value),
  },
  {
    field: 'codeVersion',
    headerName: 'Code version',
    flex: 0.5,
    minWidth: 130,
    valueGetter: (value?: string | null) => shortSha(value),
    renderCell: ({ row }: GridRenderCellParams) => (
      <Stack direction='row' spacing={0.5} sx={{ alignItems: 'center' }}>
        <span>{shortSha(row.codeVersion)}</span>
        {row.isMixedVersion && (
          <Tooltip title='A deploy landed mid-run, so its steps ran on different commits'>
            <span>
              <Icon icon='exclamation-triangle' size='sm' />
            </span>
          </Tooltip>
        )}
      </Stack>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.5,
    minWidth: 140,
    valueGetter: (value: BacktestRunStatus) =>
      evaluationChipLabel('run', value),
    renderCell: ({ row }: GridRenderCellParams) => (
      <EvaluationChip kind='run' value={row.status} />
    ),
  },
  countColumn('matched', 'Matched'),
  countColumn('mismatched', 'Mismatched'),
  countColumn('missing', 'Missing'),
  countColumn('unexpected', 'Unexpected'),
];

const BacktestDetails = ({
  backtest,
  onOpenSource,
}: {
  backtest: Backtest;
  onOpenSource?: (sourceID: string) => void;
}) => (
  <DetailList>
    <DetailRow label='Source'>
      {onOpenSource ? (
        <Link
          component='button'
          variant='body2'
          onClick={() => onOpenSource(backtest.sourceId)}
        >
          {backtest.sourceLabel}
        </Link>
      ) : (
        backtest.sourceLabel
      )}
    </DetailRow>
    <DetailRow label='Transcript'>
      {TRANSCRIPT_CHOICE_LABEL[backtest.transcript]}
    </DetailRow>
    {backtest.transcriptId && (
      <DetailRow label='Transcript ID' isID>
        {backtest.transcriptId}
      </DetailRow>
    )}
    <DetailRow label='Created'>{formatDateTime(backtest.createdAt)}</DetailRow>
  </DetailList>
);

export interface AdminBacktestPageProps {
  backtest?: Backtest;
  /** Most recently requested first. */
  runs: BacktestRunSummary[];
  isLoading?: boolean;
  isError?: boolean;
  isRunning?: boolean;
  onRunAgain: () => void;
  onBack: () => void;
  /** Opens a run from the table when given. */
  onOpenRun?: (runID: string) => void;
  /** Links the source when given. */
  onOpenSource?: (sourceID: string) => void;
}

export const AdminBacktestPage = ({
  backtest,
  runs,
  isLoading = false,
  isError = false,
  isRunning = false,
  onRunAgain,
  onBack,
  onOpenRun,
  onOpenSource,
}: AdminBacktestPageProps) => (
  <ModalPageTemplate onBack={onBack}>
    <AdminTablePage title='Backtest'>
      {isLoading && <CircularProgress size={24} />}
      {!isLoading && (isError || !backtest) && (
        <Alert severity='error'>This backtest could not be loaded.</Alert>
      )}
      {!isLoading && !isError && backtest && (
        <Stack spacing={3}>
          <BacktestDetails backtest={backtest} onOpenSource={onOpenSource} />
          <DetailSection
            title='Runs'
            actions={
              <Button
                variant='contained'
                size='small'
                onClick={onRunAgain}
                disabled={isRunning}
                startIcon={<Icon icon='play' size='sm' />}
              >
                {runs.length === 0 ? 'Run' : 'Run again'}
              </Button>
            }
          >
            <DataTable
              columns={columns}
              rows={runs}
              rowsPerPage={ROWS_PER_PAGE}
              isSearchable={false}
              noDataMsg='This backtest hasn’t run yet'
              defaultAction={
                onOpenRun && {
                  label: 'Open',
                  icon: 'chevron-right',
                  onPress: (run: BacktestRunSummary) => onOpenRun(run.id),
                }
              }
            />
          </DetailSection>
        </Stack>
      )}
    </AdminTablePage>
  </ModalPageTemplate>
);

AdminBacktestPage.displayName = 'AdminBacktestPage';
export default AdminBacktestPage;
